// Turn banner component with initiative tracker

/**
 * @typedef {import('../../types/jsdoc-types.js').TurnState} TurnState
 * @typedef {import('../../types/jsdoc-types.js').CharacterAction} CharacterAction
 * @typedef {import('../../types/jsdoc-types.js').Token} Token
 * @typedef {import('../../types/jsdoc-types.js').Character} Character
 */

import { css, html } from 'lit';
import { getBackendUrl } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import './d20-die.js';

/**
 * Turn banner component
 * Displays turn info, resources, and initiative tracker in combat
 */
class TurnBanner extends BaseComponent {
  static properties = {
    show: { type: Boolean },
    turnState: { type: Object, attribute: 'turn-state' },
    currentUserId: { type: String, attribute: 'current-user-id' },
    spectator: { type: Boolean },
    players: { type: Array },
    actions: { type: Array },
    monsterPlacements: { type: Array },
    playerCharacters: { type: Array, attribute: 'player-characters' },
    // Death save d20 animation state. While _deathSaveRolling is true the
    // pip display uses _deathSavePreRollPips (snapshotted at click time) so
    // it doesn't jump ahead of the animation.
    _deathSaveRolling: { type: Boolean, state: true },
    _deathSaveFinalRoll: { type: Number, state: true },
    _deathSaveOutcome: { type: String, state: true },
    _deathSavePreRollPips: { type: Object, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .turn-banner-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .turn-banner {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;
      gap: 16px;
      padding: 12px 24px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      border-radius: 8px;
      border: 1px solid #444;
      backdrop-filter: blur(4px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      font-size: 14px;
      white-space: nowrap;
    }

    .dying-title {
      flex-basis: 100%;
      text-align: center;
      font-weight: 600;
      font-size: 18px;
      color: #fff;
      margin-bottom: -18px;
    }

    /* Fixed-size cell that holds either the Roll Death Save button or
       the d20-die. Both are centered within it so swapping between them
       doesn't shift the banner layout. */
    .death-save-action-cell {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 130px;
      height: 52px;
    }

    .death-save-result {
      font-size: 13px;
      font-weight: 600;
      color: #fff;
      white-space: nowrap;
      animation: death-save-result-fade 0.4s ease-out;
    }

    @keyframes death-save-result-fade {
      from { opacity: 0; transform: translateX(-4px); }
      to { opacity: 1; transform: translateX(0); }
    }

    .turn-banner.your-turn.exploration-mode {
      background: rgba(59, 130, 246, 0.95);
    }

    .turn-banner.your-turn.combat-mode {
      background: rgba(220, 38, 38, 0.95);
    }

    .turn-banner.combat-mode:not(.your-turn) {
      background: rgba(127, 29, 29, 0.9);
    }

    .turn-info {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .turn-status {
      font-size: 15px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .hourglass-wrapper,
    .mode-icon-wrapper {
      position: relative;
      display: inline-flex;
      align-items: center;
    }

    .mode-tooltip,
    .hourglass-tooltip {
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.9);
      color: #fff;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      white-space: nowrap;
      margin-top: 8px;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s;
      z-index: 1001;
    }

    .hourglass-wrapper:hover .hourglass-tooltip,
    .mode-icon-wrapper:hover .mode-tooltip {
      opacity: 1;
    }

    .turn-label {
      font-weight: 600;
    }

    .turn-separator {
      opacity: 0.5;
    }

    .mode-indicator {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .mode-label {
      font-weight: 600;
      font-size: 15px;
    }

    .resource {
      font-size: 13px;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .icon-unused,
    .icon-used {
      color: rgba(255, 255, 255, 1);
      stroke-width: 2;
    }

    .action-btn {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 8px;
      background: rgba(255, 255, 255, 0.1);
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 4px;
      font-size: 13px;
      cursor: pointer;
      transition: all 0.2s;
      white-space: nowrap;
    }

    .action-btn:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.3);
    }

    .action-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .action-btn.disabled {
      opacity: 0.6;
    }

    .end-turn-btn {
      padding: 6px 16px;
      background: rgba(255, 255, 255, 0.2);
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 6px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
      white-space: nowrap;
    }

    .end-turn-btn:hover {
      background: rgba(255, 255, 255, 0.3);
      border-color: rgba(255, 255, 255, 0.5);
    }

    .end-turn-btn:active {
      transform: scale(0.95);
    }

    /* Initiative Tracker */
    .initiative-tracker {
      display: flex;
      align-items: flex-start;
      justify-content: center;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: -8px;
      position: relative;
      z-index: 1;
    }

    .initiative-entry {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .initiative-portrait {
      position: relative;
      width: 40px;
      height: 40px;
      border-radius: 8px;
      overflow: visible;
      border: 2px solid white;
      background: white;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
      transition:
        width 0.3s ease-in-out,
        height 0.3s ease-in-out;
    }

    .initiative-entry.is-current .initiative-portrait {
      width: 54px;
      height: 54px;
    }

    .initiative-portrait img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 6px;
    }

    .portrait-placeholder {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.1);
      color: rgba(0, 0, 0, 0.6);
      font-weight: bold;
      font-size: 16px;
      border-radius: 6px;
    }

    .portrait-count {
      position: absolute;
      top: -6px;
      left: -6px;
      background: rgba(0, 0, 0, 0.75);
      color: white;
      font-size: 9px;
      font-weight: bold;
      padding: 1px 4px;
      border-radius: 6px;
      min-width: 14px;
      text-align: center;
      transition: all 0.3s ease-in-out;
    }

    .initiative-entry.is-current .portrait-count {
      top: -8px;
      left: -8px;
      font-size: 11px;
      padding: 2px 5px;
      min-width: 18px;
    }

    .portrait-defeated {
      position: absolute;
      bottom: -8px;
      right: -8px;
      background: rgba(0, 0, 0, 0.85);
      color: white;
      padding: 3px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .initiative-number {
      font-size: 11px;
      font-weight: bold;
      margin-top: 2px;
      color: white;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
    }

    .initiative-entry.is-defeated {
      opacity: 0.5;
    }

    .icon {
      width: 16px;
      height: 16px;
    }

    .icon-lg {
      width: 24px;
      height: 24px;
    }

    .icon-xl {
      width: 28px;
      height: 28px;
    }

    .icon-sm {
      width: 14px;
      height: 14px;
    }
  `;

  constructor() {
    super();
    this.show = true;
    /** @type {TurnState | null} */
    this.turnState = null;
    this.currentUserId = '';
    this.spectator = false;
    /** @type {Array<{id: string, userId?: string, username?: string}>} */
    this.players = [];
    /** @type {Array<CharacterAction & {slug?: string, displayData?: {kind?: string, attackModifier?: number, damageDice?: string, damageModifier?: number, slotsRemaining?: number, slotsMax?: number}}>} */
    this.actions = [];
    /** @type {Array<*>} */
    this.monsterPlacements = [];
    /** @type {Array<Character>} */
    this.playerCharacters = [];
    // Death save d20 roll animation state.
    this._deathSaveRolling = false;
    /** @type {number | null} */
    this._deathSaveFinalRoll = null;
    /** @type {'pending' | 'success' | 'failure' | 'critical-success' | 'critical-failure'} */
    this._deathSaveOutcome = 'pending';
    /** @type {{successes: number, failures: number} | null} */
    this._deathSavePreRollPips = null;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this._deathSaveSettleTimer = null;
  }

  // @ts-expect-error - BaseComponent.connectedCallback is async
  async connectedCallback() {
    await super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._deathSaveSettleTimer) {
      clearTimeout(this._deathSaveSettleTimer);
      this._deathSaveSettleTimer = null;
    }
  }

  /**
   * @param {Map<string, unknown>} changedProps
   */
  updated(changedProps) {
    super.updated?.(changedProps);
    // When a roll is in flight, watch for the backend response by checking
    // _myCharacter.lastActionResult. The roll value comes from the structured
    // `roll` field and the detailed outcome from `resultDetail` (e.g.
    // "critical-success", "success", "failure", "critical-failure").
    if (this._deathSaveRolling && this._deathSaveFinalRoll === null && changedProps.has('playerCharacters')) {
      const me = this._myCharacter;
      const lar = me?.lastActionResult;
      if (lar && typeof lar.roll === 'number' && lar.roll > 0) {
        const detail = lar.resultDetail || '';
        let outcome = 'success';
        if (detail.startsWith('critical-success')) outcome = 'critical-success';
        else if (detail.startsWith('critical-failure')) outcome = 'critical-failure';
        else if (detail.startsWith('failure')) outcome = 'failure';
        this._settleDeathSaveDie(lar.roll, /** @type {any} */ (outcome));
      }
    }
  }

  get _isYourTurn() {
    if (this.spectator) return false;
    if (!this.currentUserId || !this.turnState) return false;
    return this.turnState.currentPlayerID === this.currentUserId;
  }

  get _yourResources() {
    if (!this.currentUserId || !this.turnState) return null;
    return this.turnState.turnResources?.[this.currentUserId];
  }

  get _isAwaitingDeathSave() {
    return !!this._yourResources?.awaitingDeathSave;
  }

  /**
   * True when the dying-state UI (title + pips + die/button) should be
   * rendered. Stays true during the death-save roll animation even after
   * the backend advances the turn off the dying PC — otherwise the die
   * would pop out mid-animation as soon as currentPlayerID flipped.
   */
  get _showDeathSaveUI() {
    return this._deathSaveRolling || (this._isYourTurn && this._isAwaitingDeathSave);
  }

  /**
   * Find the current user's own character (the dying one) from the
   * playerCharacters array so the Roll Death Save UI can display the
   * accumulated death save successes / failures alongside the button.
   */
  get _myCharacter() {
    if (!this.currentUserId || !this.playerCharacters) return null;
    return this.playerCharacters.find((c) => c.playerId === this.currentUserId) || null;
  }

  _renderResourcePips() {
    const pip = (spent) => html`
      <span
        style="display:inline-flex;align-items:center;justify-content:center;width:13px;height:13px;border-radius:50%;background:${
          spent ? '#22c55e' : 'transparent'
        };border:1.5px solid #fff;color:#fff;font-size:9px;line-height:1;font-weight:bold;flex-shrink:0;"
      >${spent ? '✓' : ''}</span>
    `;
    const cell = (spent, label) => html`
      <span style="display:inline-flex;align-items:center;gap:6px;line-height:1;">
        ${pip(spent)}<span>${label}</span>
      </span>
    `;
    // Reactions are tracked per-actor on turnState.reactionsUsed,
    // keyed by character ID (not user ID). Look up the current user's
    // character to find their reaction state.
    const myCharId = this._myCharacter?.id;
    const reactionUsed = !!(myCharId && this.turnState?.reactionsUsed?.[myCharId]);
    return html`
      <span
        style="display:inline-grid;grid-template-rows:auto auto;grid-auto-flow:column;column-gap:10px;row-gap:2px;font-size:11px;"
      >
        ${cell(this._yourResources.actionUsed, 'Action')}
        ${cell(this._yourResources.bonusActionUsed, 'Bonus')}
        ${cell(reactionUsed, 'Reaction')}
        ${cell(this._yourResources.objectInteractionUsed, 'Interaction')}
      </span>
    `;
  }

  _renderDeathSavePips() {
    const me = this._myCharacter;
    // While rolling, freeze the pip counts at their pre-roll state so the
    // die animation can resolve before the UI visually updates.
    const successes = this._deathSaveRolling
      ? (this._deathSavePreRollPips?.successes ?? 0)
      : me?.deathSaveSuccesses || 0;
    const failures = this._deathSaveRolling ? (this._deathSavePreRollPips?.failures ?? 0) : me?.deathSaveFailures || 0;

    const pip = (filled, color) => html`
      <span
        style="display:inline-block;width:13px;height:13px;border-radius:50%;margin:0 2px;background:${
          filled ? color : '#1f2937'
        };border:2px solid #fff;vertical-align:middle;"
      ></span>
    `;
    const successPips = [0, 1, 2].map((i) => pip(i < successes, '#22c55e'));
    const failurePips = [0, 1, 2].map((i) => pip(i < failures, '#ef4444'));

    const glyph = (char, color) => html`
      <span
        style="display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;border-radius:50%;background:#fff;color:${color};font-size:11px;font-weight:bold;line-height:1;margin-right:4px;"
      >
        ${char}
      </span>
    `;

    return html`
      <span style="display:inline-flex;flex-direction:column;gap:3px;font-size:0.9em;">
        <span style="display:inline-flex;align-items:center;">${glyph('✓', '#16a34a')}${successPips}</span>
        <span style="display:inline-flex;align-items:center;">${glyph('✗', '#dc2626')}${failurePips}</span>
      </span>
    `;
  }

  /**
   * Render the d20 die in place of the Roll Death Save button while an
   * animation is in flight. Delegates the spin/settle animation to the
   * shared <d20-die> component; we just pass through the final roll and
   * outcome once they're known. The death save result text ("Success 1/3",
   * "You have died.", etc.) is rendered next to the die.
   */
  _renderDeathSaveDie() {
    const settled = this._deathSaveFinalRoll !== null;

    // Build result text shown next to the die once it settles.
    let resultText = '';
    if (settled) {
      const me = this._myCharacter;
      const s = me?.deathSaveSuccesses || 0;
      const f = me?.deathSaveFailures || 0;
      switch (this._deathSaveOutcome) {
        case 'critical-success':
          resultText = 'Critical! You awaken!';
          break;
        case 'success':
          resultText = s >= 3 ? 'Stabilized!' : `Success (${s}/3)`;
          break;
        case 'failure':
          resultText = f >= 3 ? 'You have died.' : `Failure (${f}/3)`;
          break;
        case 'critical-failure':
          resultText = f >= 3 ? 'Critical! You have died.' : `Critical! Two failures (${f}/3)`;
          break;
      }
    }

    return html`
      <d20-die
        .spinning="${!settled}"
        .finalFace="${this._deathSaveFinalRoll}"
        outcome="${this._deathSaveOutcome || 'neutral'}"
      ></d20-die>
      ${resultText ? html`<span class="death-save-result">${resultText}</span>` : ''}
    `;
  }

  _handleRollDeathSave() {
    if (this._deathSaveRolling) return;

    // Snapshot the pre-roll pip counts so the pip display stays frozen
    // during the animation. Without this the pips would jump forward as
    // soon as the backend response came back and the shared
    // playerCharacters state updated.
    const me = this._myCharacter;
    this._deathSavePreRollPips = {
      successes: me?.deathSaveSuccesses || 0,
      failures: me?.deathSaveFailures || 0,
    };
    this._deathSaveRolling = true;
    this._deathSaveFinalRoll = null;
    this._deathSaveOutcome = 'pending';

    // TODO(death-saves-audio): play a "rolling" sfx loop here, stop it in
    // _settleDeathSaveDie.

    // The <d20-die> component manages the spinning face animation itself;
    // we just pass spinning=true until the roll comes back, then set
    // finalFace to freeze it on the rolled value.

    this.dispatchEvent(
      new CustomEvent('execute-action', {
        detail: { id: 'pc:roll-death-save', targeting: { type: 'self' } },
        bubbles: true,
      }),
    );
  }

  /**
   * Called from updated() once we detect the backend returned a roll (via
   * a fresh lastActionResult on _myCharacter). Keeps the spin animation
   * going for a bit longer so it feels weighty, then locks the die on
   * the final face with an outcome color and holds it before clearing
   * the rolling state.
   *
   * Flipping _deathSaveFinalRoll / _deathSaveOutcome is deferred until
   * the settle moment — the render logic reads those to decide between
   * the .spinning and .settled CSS classes, so setting them early would
   * freeze the die the instant the response arrives.
   *
   * @param {number} rollValue
   * @param {'success' | 'failure' | 'critical-success' | 'critical-failure'} outcome
   */
  _settleDeathSaveDie(rollValue, outcome) {
    if (!this._deathSaveRolling) return;
    if (this._deathSaveFinalRoll !== null) return;
    // Remember that a settle is already queued so a second incoming
    // update doesn't schedule a duplicate.
    if (this._deathSaveSettleTimer) return;

    // Keep spinning visibly for this long after the response arrives.
    const remainingSpin = 750;
    this._deathSaveSettleTimer = setTimeout(() => {
      // Freeze the die on the rolled face with an outcome color. The
      // <d20-die> component stops its own spin when finalFace is set.
      this._deathSaveFinalRoll = rollValue;
      this._deathSaveOutcome = outcome;
      // TODO(death-saves-audio): play a success / failure sting here.

      // 3. Hold on the final face so the player can read it, then tear
      //    down the rolling overlay. By this point the backend has also
      //    advanced the turn, so clearing naturally reveals the next
      //    actor.
      this._deathSaveSettleTimer = setTimeout(() => {
        this._deathSaveRolling = false;
        this._deathSaveFinalRoll = null;
        this._deathSaveOutcome = 'pending';
        this._deathSavePreRollPips = null;
        this._deathSaveSettleTimer = null;
      }, 2500);
    }, remainingSpin);
  }

  get _isNPCTurn() {
    if (!this._isCombatTime) return false;
    return !this.turnState.currentPlayerID && this.turnState.currentActorID;
  }

  // Always-on initiative (#358) replaces the binary mode/combatState model
  // with three flat fields on turnState:
  //   combatTime: bool — sticky-true while combat is active
  //   hostileVisible: bool — at least one currently-visible monster is
  //     hostile (drives the red bar treatment)
  //   initiativeOrder/currentIndex/roundNumber — the always-on initiative
  // Three rendering states:
  //   exploration             — !combatTime; PC slots, no numbers
  //   combat-time, peaceful   — combatTime && !hostileVisible
  //   combat-time, hostile    — hostileVisible (red treatment)

  get _isCombatTime() {
    return !!this.turnState?.combatTime;
  }

  get _isHostileCombat() {
    return !!this.turnState?.hostileVisible;
  }

  get _modeLabel() {
    if (this._isHostileCombat) return 'Combat';
    if (this._isCombatTime) return 'Initiative';
    return 'Exploration';
  }

  get _modeTooltip() {
    if (this._isHostileCombat) return 'Combat — initiative order';
    if (this._isCombatTime) return 'Initiative order — no hostiles in sight';
    return 'Exploration';
  }

  get _turnIndicatorTooltip() {
    if (this._isCombatTime) {
      return `Round ${this.turnState?.roundNumber ?? 1}`;
    }
    return `Turn ${this.turnState?.turnNumber || 1}`;
  }

  get _currentActorName() {
    if (!this.turnState) return 'Unknown';
    if (this._isCombatTime) {
      const currentIndex = this.turnState.currentIndex;
      const initiativeOrder = this.turnState.initiativeOrder;
      if (initiativeOrder?.[currentIndex]) {
        return initiativeOrder[currentIndex].name;
      }
    }
    const currentPlayerId = this.turnState.currentPlayerID;
    const player = this.players.find((p) => (p.id || p.userId) === currentPlayerId);
    return player?.username || 'Unknown';
  }

  get _currentPlayerName() {
    if (!this.turnState) return 'Unknown';
    const currentPlayerId = this.turnState.currentPlayerID;
    const player = this.players.find((p) => (p.id || p.userId) === currentPlayerId);
    return player?.username || 'Unknown';
  }

  /** @param {string} name */
  _getPossessive(name) {
    return name.endsWith('s') ? `${name}'` : `${name}'s`;
  }

  get _initiativeDisplayOrder() {
    // Always-on initiative (#358): the initiative tracker renders in every
    // state, but during exploration it only contains PC entries (the
    // server's full initiative order is filtered to "visible to the party"
    // at the fan-out boundary). The PC-only path keeps everything else
    // about the row layout the same — what changes in the markup is the
    // initiative number, hidden when !combatTime.
    if (!this.turnState?.initiativeOrder) return [];

    const inCombat = this._isCombatTime;
    const initiativeOrder = this.turnState.initiativeOrder;
    const currentIndex = this.turnState.currentIndex ?? 0;
    const result = [];
    const addedSlugs = new Set();

    for (let i = 0; i < initiativeOrder.length; i++) {
      const entry = initiativeOrder[i];
      if (!entry) continue;

      // Out of combat → PC slots only. The server keeps NPC entries in
      // initiativeOrder across combat:end (they re-roll on the next
      // combat:start), so without this filter the bar would show the
      // last combat's NPCs (defeated and all) until the next combat
      // begins.
      if (!inCombat && entry.actorType !== 'pc') continue;

      if (entry.actorType === 'pc') {
        const pc = this.playerCharacters?.find((c) => c.id === entry.actorId);
        result.push({
          ...entry,
          imageUrl: this._resolveImageUrl(pc?.token?.imageUrl || ''),
          isCurrent: i === currentIndex,
          count: 1,
          allDefeated: false,
        });
      } else {
        let placement = this.monsterPlacements?.find((p) => p.id === entry.actorId);
        if (!placement) {
          placement = this.monsterPlacements?.find((p) => p.name === entry.name);
        }

        // Placeholder placements (#253): the party only senses them
        // adjacent in the dark. In the initiative bar they share a
        // single anonymous group so the banner doesn't leak the
        // monster name or token image through the side door.
        const isPlaceholder = !!placement?.placeholder;
        const placeholderGroupKey = '__placeholder__';
        const groupKey = isPlaceholder ? placeholderGroupKey : placement?.slug || entry.name;

        if (addedSlugs.has(groupKey)) continue;
        addedSlugs.add(groupKey);

        let groupPlacements;
        if (isPlaceholder) {
          groupPlacements = this.monsterPlacements?.filter((p) => p.placeholder) || [];
        } else {
          groupPlacements =
            this.monsterPlacements?.filter((p) => !p.placeholder && (p.slug || p.name) === groupKey) || [];
        }
        const defeatedCount = groupPlacements.filter((p) => p.defeated).length;
        const totalInGroup = groupPlacements.length;
        const allDefeated = totalInGroup > 0 && defeatedCount === totalInGroup;

        const groupEntries = initiativeOrder
          .map(
            (
              /** @type {{ actorId: string, actorType: string, initiative: number, name: string }} */ e,
              /** @type {number} */ idx,
            ) => ({ entry: e, index: idx }),
          )
          .filter(
            (
              /** @type {{ entry: { actorType: string, actorId: string, name: string }, index: number }} */ {
                entry: e,
              },
            ) => {
              if (e.actorType !== 'npc') return false;
              const p =
                this.monsterPlacements?.find((pp) => pp.id === e.actorId) ||
                this.monsterPlacements?.find((pp) => pp.name === e.name);
              if (isPlaceholder) return !!p?.placeholder;
              if (p?.placeholder) return false;
              const eGroupKey = p?.slug || e.name;
              return eGroupKey === groupKey;
            },
          );

        const isGroupCurrent = groupEntries.some(
          (
            /** @type {{ entry: { actorType: string, actorId: string, name: string }, index: number }} */ {
              index: idx,
            },
          ) => idx === currentIndex,
        );

        result.push({
          actorId: entry.actorId,
          actorType: 'npc',
          initiative: entry.initiative,
          name: isPlaceholder ? 'Shape in darkness' : entry.name,
          imageUrl: isPlaceholder ? '' : this._resolveImageUrl(placement?.imageUrl || ''),
          isCurrent: isGroupCurrent,
          count: groupEntries.length,
          slug: groupKey,
          allDefeated,
          placeholder: isPlaceholder,
        });
      }
    }

    return result;
  }

  /** @param {string} url */
  _resolveImageUrl(url) {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const backendUrl = getBackendUrl();
    return backendUrl ? backendUrl + url : url;
  }

  _showActionsModal() {
    this.dispatchEvent(new CustomEvent('show-actions-modal', { bubbles: true, composed: true }));
  }

  _handleEndTurn() {
    this.dispatchEvent(new CustomEvent('end-turn', { bubbles: true }));
  }

  // Icon rendering methods
  _renderMapIcon() {
    return html`
      <svg
        class="icon-xl"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
        <line x1="9" y1="3" x2="9" y2="18" />
        <line x1="15" y1="6" x2="15" y2="21" />
      </svg>
    `;
  }

  _renderSwordsIcon() {
    return html`
      <svg
        class="icon-xl"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5" />
        <line x1="13" y1="19" x2="19" y2="13" />
        <line x1="16" y1="16" x2="20" y2="20" />
        <line x1="19" y1="21" x2="21" y2="19" />
        <polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5" />
        <line x1="5" y1="14" x2="9" y2="18" />
        <line x1="7" y1="17" x2="4" y2="20" />
        <line x1="3" y1="19" x2="5" y2="21" />
      </svg>
    `;
  }

  _renderHourglassIcon() {
    return html`
      <svg
        class="icon-lg"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M5 22h14" />
        <path d="M5 2h14" />
        <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
        <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
      </svg>
    `;
  }

  _renderFootprintsIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path
          d="M4 16v-2.38C4 11.5 2.97 10.5 3 8c.03-2.72 1.49-6 4.5-6C9.37 2 10 3.8 10 5.5c0 3.11-2 5.66-2 8.68V16a2 2 0 1 1-4 0Z"
        />
        <path
          d="M20 20v-2.38c0-2.12 1.03-3.12 1-5.62-.03-2.72-1.49-6-4.5-6C14.63 6 14 7.8 14 9.5c0 3.11 2 5.66 2 8.68V20a2 2 0 1 0 4 0Z"
        />
        <path d="M16 17h4" />
        <path d="M4 13h4" />
      </svg>
    `;
  }

  _renderCircleIcon() {
    return html`
      <svg
        class="icon icon-unused"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
      </svg>
    `;
  }

  _renderCircleCheckIcon() {
    return html`
      <svg
        class="icon icon-used"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    `;
  }

  _renderChevronDownIcon() {
    return html`
      <svg
        class="icon-sm"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    `;
  }

  _renderSkullIcon() {
    return html`
      <svg
        class="icon-sm"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle cx="9" cy="12" r="1" />
        <circle cx="15" cy="12" r="1" />
        <path d="M8 20v2h8v-2" />
        <path d="m12.5 17-.5-1-.5 1h1z" />
        <path d="M16 20a2 2 0 0 0 1.56-3.25 8 8 0 1 0-11.12 0A2 2 0 0 0 8 20" />
      </svg>
    `;
  }

  render() {
    if (!this.show || !this.turnState) {
      return html``;
    }

    const bannerClasses = [
      'turn-banner',
      this._isYourTurn || this._deathSaveRolling ? 'your-turn' : '',
      this._isCombatTime ? 'combat-mode' : 'exploration-mode',
      this._isHostileCombat ? 'hostile' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return html`
      <div class="turn-banner-wrapper">
        <div class="${bannerClasses}">
          ${
            this._showDeathSaveUI
              ? html`
                <div class="dying-title">
                  <span style="display:inline-flex;align-items:center;gap:8px;">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="26"
                      height="26"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <circle cx="9" cy="12" r="1" />
                      <circle cx="15" cy="12" r="1" />
                      <path d="M8 20v2h8v-2" />
                      <path d="m12.5 17-.5-1-.5 1h1z" />
                      <path d="M16 20a2 2 0 0 0 1.56-3.25 8 8 0 1 0-11.12 0A2 2 0 0 0 8 20" />
                    </svg>
                    You Are Dying!
                  </span>
                </div>
              `
              : ''
          }
          <div class="turn-info">
            <!-- Mode indicator -->
            <div class="mode-indicator">
              <span class="mode-icon-wrapper">
                ${this._isCombatTime ? this._renderSwordsIcon() : this._renderMapIcon()}
                <span class="mode-tooltip">${this._modeTooltip}</span>
              </span>
              <span class="mode-label">${this._modeLabel}</span>
            </div>

            <span class="turn-separator">|</span>

            <!-- Turn/Round indicator -->
            <div class="turn-status">
              <span class="hourglass-wrapper">
                ${this._renderHourglassIcon()}
                <span class="hourglass-tooltip">${this._turnIndicatorTooltip}</span>
              </span>
              ${
                this._isYourTurn
                  ? html` <span class="turn-label">Your Turn</span> `
                  : this._isNPCTurn
                    ? html` <span class="turn-label">${this._getPossessive(this._currentActorName)} Turn</span> `
                    : this._isCombatTime && !this.turnState?.currentActorID
                      ? html` <span class="turn-label">Combat continues…</span> `
                      : html`
                        <span class="turn-label">Waiting for ${this._getPossessive(this._currentPlayerName)} turn</span>
                      `
              }
            </div>

            <!-- Resources (or the death save UI while rolling / dying) -->
            ${
              this._showDeathSaveUI
                ? html`
                  <!-- Dying PC: the only legal action is Roll Death Save.
                       The "YOU ARE DYING" title is rendered above as a
                       full-width row. Here we just render the pips and
                       the button / die inline. Uses | separator to match
                       the mode↔turn separator style, since the death
                       save UI replaces the whole resources section.
                       Kept visible during the roll animation even after
                       the backend advances the turn off this PC — see
                       _showDeathSaveUI. -->
                  <span class="turn-separator">|</span>
                  ${this._renderDeathSavePips()}
                  <span class="death-save-action-cell">
                    ${
                      this._deathSaveRolling
                        ? this._renderDeathSaveDie()
                        : html`
                          <button
                            class="action-btn death-save-btn"
                            style="background:#7f1d1d;border-color:#f87171;color:#fff;font-weight:bold;"
                            @click="${this._handleRollDeathSave}"
                          >
                            Roll Death Save
                          </button>
                        `
                    }
                  </span>
                `
                : this._isYourTurn && this._yourResources
                  ? html`
                    <span class="turn-separator">&bull;</span>
                    <span class="resource">
                      ${this._renderFootprintsIcon()}
                      <strong>${this._yourResources.movementRemaining}ft</strong>
                    </span>
                    <span class="turn-separator">&bull;</span>

                    <!-- Actions button: opens the unified actions modal.
                         Disabled only when no entry in the list is
                         currently enabled. Resource state lives in the
                         pip column to the right, not on the button. -->
                    <button
                      class="action-btn"
                      ?disabled="${!this.actions?.some((a) => a.enabled)}"
                      @click="${this._showActionsModal}"
                    >
                      Actions
                      ${this.actions?.some((a) => a.enabled) ? this._renderChevronDownIcon() : ''}
                    </button>

                    <span class="turn-separator">&bull;</span>

                    <!-- Action / Bonus / Reaction resource pips, stacked.
                         Visually matches the death-save pips: 13x13
                         circle filled when spent, dark when available,
                         label to its right, font scaled down for
                         vertical compactness. -->
                    ${this._renderResourcePips()}
                  `
                  : ''
            }
          </div>

          <!-- End Turn button (hidden while awaiting a death save or
               during the roll animation — the roll itself auto-ends
               the turn) -->
          ${
            this._isYourTurn && !this._isAwaitingDeathSave && !this._deathSaveRolling
              ? html` <button class="end-turn-btn" @click="${this._handleEndTurn}">End Turn</button> `
              : ''
          }
        </div>

        <!-- Initiative tracker (always rendered when an order exists; the
             initiative-number is hidden during exploration so the bar shows
             PC slots in order without the d20 totals). -->
        ${
          this._initiativeDisplayOrder.length > 0
            ? html`
              <div class="initiative-tracker">
                ${this._initiativeDisplayOrder.map(
                  (entry) => html`
                    <div
                      class="initiative-entry ${entry.isCurrent ? 'is-current' : ''} ${
                        entry.allDefeated ? 'is-defeated' : ''
                      }"
                      title="${entry.count > 1 ? `${entry.name} (\u00d7${entry.count})` : entry.name}"
                    >
                      <div class="initiative-portrait">
                        ${
                          entry.imageUrl
                            ? html` <img src="${entry.imageUrl}" alt="${entry.name}" /> `
                            : html` <div class="portrait-placeholder">${entry.placeholder ? '?' : entry.name[0]}</div> `
                        }
                        ${
                          entry.allDefeated
                            ? html` <span class="portrait-defeated">${this._renderSkullIcon()}</span> `
                            : entry.count > 1
                              ? html` <span class="portrait-count">×${entry.count}</span> `
                              : ''
                        }
                      </div>
                      ${this._isCombatTime ? html`<span class="initiative-number">${entry.initiative}</span>` : ''}
                    </div>
                  `,
                )}
              </div>
            `
            : ''
        }
      </div>
    `;
  }
}

customElements.define('turn-banner', TurnBanner);
